<template>
  <div>
    <v-data-table
      :headers="computedHeaders"
      :items="borrows"
      :loading="loading"
      item-key="id"
      no-data-text="Không có phiếu mượn nào"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-text': 'Hiển thị mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page-options': [10, 20, 50]
      }"
      @update:options="onOptionsChange"
    >
      <template v-slot:item.code="{ item }">
        <a class="font-weight-bold primary--text" @click="onViewDetail(item)">{{ item.code }}</a>
      </template>

      <template v-slot:item.itemCount="{ item }">
        <span>{{ (item.items && item.items.length) || 0 }} thiết bị</span>
      </template>

      <template v-slot:item.timeRange="{ item }">
        <div class="text-no-wrap">
          <span>{{ formatBorrowDateTime(item.borrowDate, item.borrowTime) }}</span>
          <span class="mx-1 grey--text font-weight-bold">&rarr;</span>
          <span :class="{ 'red--text font-weight-bold': isOverdue(item) }">
            {{ formatBorrowDateTime(item.expectedReturnDate, item.expectedReturnTime) }}
          </span>
        </div>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="getStatusColor(item)" dark>
          {{ getStatusLabel(item) }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center">
          <!-- Nút Phê duyệt và Từ chối khi phiếu ở trạng thái Chờ duyệt -->
          <template v-if="!isTeacher && item.status === 'PENDING'">
            <v-btn
              icon
              small
              color="success"
              class="mr-1"
              @click="onOpenApprove(item)"
              title="Phê duyệt mượn"
            >
              <v-icon small>mdi-check-circle-outline</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              class="mr-1"
              @click="onOpenReject(item)"
              title="Từ chối mượn"
            >
              <v-icon small>mdi-close-circle-outline</v-icon>
            </v-btn>
          </template>

          <!-- Nút Bàn giao khi phiếu Đã duyệt -->
          <template v-else-if="!isTeacher && item.status === 'APPROVED'">
            <v-btn
              icon
              small
              color="indigo"
              class="mr-1"
              @click="onViewDetail(item)"
              title="Bàn giao thiết bị"
            >
              <v-icon small>mdi-hand-coin</v-icon>
            </v-btn>
          </template>

          <!-- Nút Nhận lại thiết bị khi Đang mượn -->
          <template v-else-if="!isTeacher && item.status === 'BORROWING'">
            <v-btn
              icon
              small
              color="teal"
              class="mr-1"
              @click="onViewDetail(item)"
              title="Xác nhận nhận lại thiết bị"
            >
              <v-icon small>mdi-keyboard-return</v-icon>
            </v-btn>
          </template>

          <v-btn icon small color="primary" class="mr-1" @click="onViewDetail(item)" title="Xem chi tiết">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>

          <v-btn
            icon
            small
            color="error"
            @click="onRemove(item)"
            title="Xóa phiếu"
            v-if="item.status === 'PENDING' || item.status === 'REJECTED'"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Dialog chi tiết phiếu mượn -->
    <BorrowDetailDialog :state="detailDialog" :item="selectedItem" @updated="refresh()" />

    <!-- Dialog phê duyệt phiếu mượn -->
    <BorrowApproveDialog :state="approveDialog" :item="selectedApproveItem" @updated="refresh()" />

    <!-- Dialog từ chối phiếu mượn -->
    <BorrowRejectDialog :state="rejectDialog" :item="selectedRejectItem" @updated="refresh()" />
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState, mapGetters } from 'vuex'
import BorrowDetailDialog from '@/modules/facility/borrow/BorrowDetailDialog.vue'
import BorrowApproveDialog from '@/modules/facility/borrow/BorrowApproveDialog.vue'
import BorrowRejectDialog from '@/modules/facility/borrow/BorrowRejectDialog.vue'

const headers = [
  { text: 'Mã phiếu', value: 'code', align: 'left', sortable: false },
  { text: 'Người mượn', value: 'borrowerName', align: 'left', sortable: false },
  { text: 'Mục đích', value: 'purpose', align: 'left', sortable: false },
  { text: 'Số món', value: 'itemCount', align: 'center', sortable: false },
  { text: 'Thời gian mượn', value: 'timeRange', align: 'center', sortable: false },
  { text: 'Trạng thái', value: 'status', align: 'center', sortable: false },
  { text: 'Thao tác', value: 'actions', align: 'center', sortable: false, width: '160px' }
]

export default {
  name: 'BorrowDataTable',
  components: {
    BorrowDetailDialog,
    BorrowApproveDialog,
    BorrowRejectDialog
  },
  data() {
    return {
      headers,
      detailDialog: false,
      selectedItem: null,
      approveDialog: false,
      selectedApproveItem: null,
      rejectDialog: false,
      selectedRejectItem: null
    }
  },
  computed: {
    ...mapState('FacilityBorrows', ['borrows', 'totalItems', 'pageText', 'loading']),
    ...mapGetters('auth', ['role', 'user']),
    isTeacher() {
      return this.role && (this.role.name === 'Teacher' || this.role.type === 'teacher')
    },
    computedHeaders() {
      if (this.isTeacher) {
        return this.headers.filter(h => h.value !== 'borrowerName')
      }
      return this.headers
    }
  },
  methods: {
    ...mapActions('FacilityBorrows', ['requestPageSettings', 'searchBorrowList', 'removeBorrow']),
    async refresh(query) {
      const q = { ...query }
      if (this.isTeacher) {
        const userId = this.user && (this.user.id || this.user._id)
        if (userId) {
          q.borrower = userId
        }
      }
      this.searchBorrowList(q)
    },
    onOptionsChange({ page, itemsPerPage }) {
      this.requestPageSettings({ page, itemsPerPage })
    },
    formatBorrowDateTime(dateStr, timeStr) {
      if (!dateStr) return '-'
      const formattedDate = moment(dateStr).isValid() ? moment(dateStr).format('DD/MM/YYYY') : dateStr
      return timeStr ? `${timeStr} ${formattedDate}` : formattedDate
    },
    isOverdue(item) {
      if (!item || item.status === 'RETURNED' || item.status === 'REJECTED') return false
      if (!item.expectedReturnDate) return false
      const now = new Date()
      const timeStr = item.expectedReturnTime || '23:59'
      const returnDateTime = new Date(`${item.expectedReturnDate}T${timeStr}:00`)
      return returnDateTime < now
    },
    getStatusLabel(item) {
      if (typeof item === 'string') {
        switch (item) {
          case 'PENDING': return 'Chờ duyệt'
          case 'APPROVED': return 'Đã duyệt'
          case 'BORROWING': return 'Đang mượn'
          case 'RETURNED': return 'Đã trả'
          case 'REJECTED': return 'Từ chối'
          case 'OVERDUE': return 'Quá hạn'
          default: return item
        }
      }
      if (item.status === 'RETURNED') return 'Đã trả'
      if (item.status === 'REJECTED') return 'Từ chối'
      if (item.status === 'OVERDUE' || this.isOverdue(item)) {
        if (item.status === 'BORROWING') return 'Quá hạn trả'
        if (item.status === 'APPROVED') return 'Quá hạn nhận'
        return 'Quá hạn'
      }
      switch (item.status) {
        case 'PENDING': return 'Chờ duyệt'
        case 'APPROVED': return 'Đã duyệt'
        case 'BORROWING': return 'Đang mượn'
        default: return item.status
      }
    },
    getStatusColor(item) {
      const status = typeof item === 'string' ? item : item.status
      if (status === 'RETURNED') return 'success'
      if (status === 'REJECTED') return 'blue-grey'
      if (typeof item === 'object' && (status === 'OVERDUE' || this.isOverdue(item))) {
        return 'error'
      }
      switch (status) {
        case 'PENDING': return 'warning'
        case 'APPROVED': return 'indigo'
        case 'BORROWING': return 'primary'
        case 'OVERDUE': return 'error'
        default: return 'blue-grey'
      }
    },
    onViewDetail(item) {
      this.selectedItem = item
      this.detailDialog = !this.detailDialog
    },
    onOpenApprove(item) {
      this.selectedApproveItem = item
      this.approveDialog = !this.approveDialog
    },
    onOpenReject(item) {
      this.selectedRejectItem = item
      this.rejectDialog = !this.rejectDialog
    },
    onRemove(item) {
      this.$dialog.confirm({
        title: 'Thông báo xác nhận',
        text: `Bạn có chắc muốn xóa phiếu mượn "${item.code}"?`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeBorrow(item.id)
            this.$alert.deleteSuccess()
          } catch (error) {
            this.$alert.deleteError(error)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>
